import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { OdinInput } from '@/components/ui/odin/form/odin-input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { required } from 'utils/validations'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { useModal } from 'providers/ModalProvider'
import { useAuthFlow } from 'hooks/useAuthFlow'

type ResetPasswordModalProps = {
  email?: string
  onSuccess?: () => void
}

const schema = z.object({
  email: required,
})

type ResetPasswordFormValues = z.infer<typeof schema>

export const ResetPasswordModal = ({ onSuccess, email: initialEmail }: ResetPasswordModalProps) => {
  const { t } = useTranslation()
  const { closeModal } = useModal()
  const { handleResetPassword, isLoading } = useAuthFlow()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { email: initialEmail ?? '' },
  })

  const onSubmit = async (values: ResetPasswordFormValues) => {
    try {
      await handleResetPassword(values, () => {
        closeModal()
        onSuccess?.()
      })
    } catch {
      // Error already handled in hook
    }
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('forgottenPassword')} icon="key" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={'space-y-1'}>
          <OdinInput label={t('email')} error={errors.email?.message} {...register('email')} />
          <div className={'h-3'} />
          <div className={'text-sm'}>{t('resetPasswordHint')}</div>
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={isLoading}>
            <span className={'text-sm'}>{t('sendEmailResetPassword')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}
